import Button from 'components/ui/Button/Button';
import Checkbox from 'components/ui/Checkbox/Checkbox';
import FormGroup from 'components/ui/FormGroup/FormGroup';
import SelectCustom from 'components/ui/SelectCustom/SelectCustom';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { FC, Fragment, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { stripTags } from 'utils/shared/utils';
import ScreenshotSupport from '../../ScreenshotSupport';
import { checkAHAVI } from 'utils/utils';

interface FreeTextModalProps {
  close: () => void;
  onSave: (
    desc: string,
    displayOrder?: number,
    cancellationSettings?: { showCancellationDate: boolean; cancellationDateDelta: number },
  ) => void;
  freeText: string;
  displayOrder?: number;
  isHotel?: boolean;
  showCancellationDate?: boolean;
  cancellationDateDelta?: number;
}

const FreeTextModal: FC<FreeTextModalProps> = ({
  close,
  onSave,
  freeText,
  isHotel,
  showCancellationDate,
  cancellationDateDelta,
  ...props
}) => {
  const { t } = useTranslation();
  const [desc, setDesc] = useState('');
  const [displayOrder, setDisplayOrder] = useState('');
  const [showCancellationDateValue, setShowCancellationDateValue] = useState(true);
  const [cancellationDateDeltaValue, setCancellationDateDeltaValue] = useState('1');
  const [isSubmitted, setIsSubmitted] = useState(false);

  useEffect(() => {
    if (freeText) {
      setDesc(freeText);
    }
    if (props.displayOrder) {
      setDisplayOrder(props.displayOrder + '');
    }
    if (showCancellationDate !== undefined) {
      setShowCancellationDateValue(showCancellationDate);
    }
    if (cancellationDateDelta !== undefined) {
      setCancellationDateDeltaValue(String(cancellationDateDelta));
    }
  }, [freeText, props.displayOrder, showCancellationDate, cancellationDateDelta]);

  const onClickSave = () => {
    setIsSubmitted(true);
    if (displayOrder.length > 2 || (desc && desc.length < 3)) {
      return;
    }

    close();
    onSave(
      stripTags(desc),
      displayOrder ? +displayOrder : undefined,
      isHotel
        ? {
          showCancellationDate: showCancellationDateValue,
          cancellationDateDelta: +cancellationDateDeltaValue,
        }
        : undefined,
    );
  };

  return (
    <Fragment>
      {createPortal(
        <div className="modal-overlay modal-overlay-top">
          <div className="modal active">
            <div className="modal-title">
              <div className="title">{t('proposal.edit')}</div>
              <div>
                <ScreenshotSupport />
                <FontAwesomeIcon icon={faTimes} onClick={close} />
              </div>
            </div>
            <div className="modal-content">
              <div className={'form-group' + (isSubmitted && desc.length < 3 ? ' is-invalid' : '')}>
                <label>{t('notes')}</label>
                <textarea
                  value={desc}
                  onChange={(e) => setDesc(e.target.value)}
                  className="form-control form-control-textarea"
                  style={{ direction: checkAHAVI(desc) ? 'rtl' : 'ltr', height: 90 }}
                  rows={3}
                />
                {isSubmitted && desc.length < 3 && (
                  <div className="form-invalid-text">{t('validation.required')}</div>
                )}
              </div>
              <FormGroup
                label={t('proposal.display_order')}
                value={displayOrder}
                onChange={setDisplayOrder}
                validation={{ numbers: true, maxLength: 2 }}
                isSubmitted={isSubmitted}
              />
              {isHotel && (
                <Fragment>
                  <div className="mt-15 mb-15">
                    <Checkbox
                      checked={showCancellationDateValue}
                      label={t('proposal.show_cancellation_date')}
                      onChange={setShowCancellationDateValue}
                    />
                  </div>
                  <div>
                    <div className='mb-5'>{t('proposal.cancellation_date_delta')}</div>
                    <SelectCustom
                      options={[0, 1, 2, 3, 4, 5].map((n) => ({
                        label: String(n),
                        value: String(n),
                      }))}
                      menuPlacement='top'
                      value={cancellationDateDeltaValue}
                      onChange={setCancellationDateDeltaValue}
                    />
                  </div>
                </Fragment>
              )}
            </div>
            <div className="modal-footer">
              <Button className="btn btn-default" title={t('close')} onClick={close} />

              <Button className="btn btn-primary" title={t('save')} onClick={onClickSave} />
            </div>
          </div>
        </div>,
        document.body
      )}
    </Fragment>
  );
};

export default FreeTextModal;
